import { useMemo } from "react";
import { useTranslation } from "@coworker/locales";
import { useCustomAllSelectOption } from "../../shared/filters/utils/useAllSelectOption";
import {
  FilterType,
  InputType,
  SelectFilter,
} from "../../shared/filters/types/filter";
import { LOCATIONS_ID } from "./useLocationsFilter";
import { GROUP_LOCATIONS_ID } from "./useGroupLocationsFilter";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

export const TASK_TYPE_ID = "taskType";

const UPCOMING_TASK_TYPES = ["custom", "productIssue"];

type TaskTypeFilterProps = {
  isUpcoming?: boolean;
};

export function useTaskTypeFilter({
  isUpcoming = false,
}: TaskTypeFilterProps = {}): SelectFilter {
  const { t } = useTranslation();
  const defaultValue = { filterId: TASK_TYPE_ID, value: "" };

  const enableTaskManagement = useFlag(FLAGS.ENABLE_TASK_MANAGEMENT);

  const allOptions = useMemo(
    () => [
      {
        value: "pickAndRefill",
        title: t("addonTitleString"),
        dependentOfFilterId: GROUP_LOCATIONS_ID,
      },
      {
        value: "refill",
        title: t("refillString"),
        dependentOfFilterId: GROUP_LOCATIONS_ID,
      },
      {
        value: "productIssue",
        title: t("taskTypeTitleProductIssue"),
        dependentOfFilterId: LOCATIONS_ID,
      },
      {
        value: "PQR",
        title: t("productQualityReportString"),
        dependentOfFilterId: LOCATIONS_ID,
      },
      {
        value: "roomSettings",
        title: t("mediaMaintenanceString"),
      },
      {
        value: "testbuy",
        title: t("testBuyFollowUpString"),
        dependentOfFilterId: LOCATIONS_ID,
      },
      {
        value: "mfaq",
        title: t("mfaqFollowUpTitle"),
        dependentOfFilterId: LOCATIONS_ID,
      },
      {
        value: "custom",
        title: t(
          `${enableTaskManagement ? "TasksString" : "customTasksString"}`
        ),
        dependentOfFilterId: LOCATIONS_ID,
      },
      {
        value: "main",
        title: t("mainScheduledTasksString"),
      },
      {
        value: "planned",
        title: t("scheduledTasksString"),
      },
    ],
    [t, enableTaskManagement]
  );

  const options = useMemo(
    () =>
      isUpcoming
        ? allOptions.filter((option) =>
            UPCOMING_TASK_TYPES.includes(option.value)
          )
        : allOptions,
    [isUpcoming, allOptions]
  );

  return useCustomAllSelectOption(
    {
      inputType: InputType.Select,
      filterType: FilterType.Filter,
      id: TASK_TYPE_ID,
      title: t("task_typeString"),
      options,
      enabled: true,
      defaultValue,
    },
    t("allTaskTypesString")
  );
}
